// 🔄 Security Sync Service - Sincronização Segura de Dados
import CryptoJS from 'crypto-js';
import SecurePasswordHash from './SecurePasswordHash';

class SecuritySyncService {
  constructor() {
    this.syncKey = 'sgv_security_sync';
    this.lastSyncKey = 'sgv_last_security_sync';
    this.syncInterval = 5 * 60 * 1000; // 5 minutos
    this.syncIntervalId = null;
  }

  /**
   * Iniciar sincronização automática de segurança
   */
  startSecuritySync() {
    // Sincronizar imediatamente
    this.syncSecurityData();

    // Configurar sincronização periódica
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
    }

    this.syncIntervalId = setInterval(() => {
      this.syncSecurityData();
    }, this.syncInterval);
  }

  /**
   * Parar sincronização
   */
  stopSecuritySync() {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
    }
  }

  /**
   * Sincronizar dados de segurança com o servidor
   */
  async syncSecurityData() {
    try {
      const lastSync = this.getLastSyncTime();
      const now = Date.now();

      // Sincronizar apenas se passou o tempo mínimo
      if (now - lastSync < this.syncInterval) {
        return;
      }

      console.log('🔄 Sincronizando dados de segurança...');

      // Obter dados de usuários com hashes
      const usersData = this.getSecureUsersData();

      // Criar pacote de sincronização seguro
      const syncPackage = {
        version: '2.0',
        timestamp: now,
        users: usersData.map(user => ({
          id: user.id,
          username: user.username,
          role: user.role,
          fullName: user.name,
          passwordHash: user.passwordHash,
          passwordSalt: user.passwordSalt,
          passwordVersion: user.passwordVersion || '2.0',
          vehicleId: user.vehicleId,
          updatedAt: user.passwordUpgradedAt || new Date().toISOString()
        })),
        checksum: this.calculateChecksum(usersData)
      };

      // Salvar no sistema de armazenamento atual (Supabase ou localStorage)
      await this.saveSyncPackage(syncPackage);

      // Atualizar timestamp da última sincronização
      this.updateLastSyncTime(now);

      console.log('✅ Sincronização de segurança concluída');

    } catch (error) {
      console.error('❌ Erro na sincronização de segurança:', error);
    }
  }

  /**
   * Obter dados de usuários com hashes seguros
   */
  getSecureUsersData() {
    try {
      const legacyUsers = JSON.parse(localStorage.getItem('INIT_USERS') || '[]');

      return legacyUsers.map(user => {
        // Se já tem hash, usar; senão, é um usuário antigo
        if (user.passwordHash && user.passwordSalt) {
          return {
            ...user,
            passwordVersion: '2.0',
            hasHash: true
          };
        } else {
          // Usuário antigo - marcar para upgrade
          return {
            ...user,
            passwordVersion: '1.0',
            hasHash: false,
            needsUpgrade: true
          };
        }
      });
    } catch (error) {
      console.error('Erro ao ler usuários:', error);
      return [];
    }
  }

  /**
   * Salvar pacote de sincronização
   */
  async saveSyncPackage(syncPackage) {
    try {
      // Tentar salvar via Supabase se disponível
      if (this.isSupabaseAvailable()) {
        await this.saveToSupabase(syncPackage);
      } else {
        // Salvar em localStorage como backup
        await this.saveToLocalStorage(syncPackage);
      }

      // Criptografar e salvar dados de sincronização
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(syncPackage),
        'SGV_SYNC_SECRET'
      ).toString();

      localStorage.setItem(this.syncKey, encryptedData);

    } catch (error) {
      console.error('Erro ao salvar pacote de sincronização:', error);
    }
  }

  /**
   * Verificar se Supabase está disponível
   */
  isSupabaseAvailable() {
    try {
      const url = process.env.REACT_APP_SUPABASE_URL;
      const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://SEU_PROJETO.supabase.co' && key !== 'SUA_CHAVE_ANON_KEY_AQUI');
    } catch {
      return false;
    }
  }

  /**
   * Salvar via Supabase
   */
  async saveToSupabase(syncPackage) {
    try {
      const { supabase } = await import('../supabase-client');

      // Salvar em tabela de segurança (ou usar users existente)
      const { error } = await supabase
        .from('security_sync')
        .upsert({
          id: 'main',
          data: syncPackage,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Erro ao salvar no Supabase:', error);
        throw error;
      }

    } catch (error) {
      console.error('Erro ao salvar via Supabase:', error);
      throw error;
    }
  }

  /**
   * Salvar em localStorage (fallback)
   */
  async saveToLocalStorage(syncPackage) {
    try {
      // Salvar dados de segurança criptografados
      const securityData = {
        users: syncPackage.users,
        lastUpdated: syncPackage.timestamp,
        checksum: syncPackage.checksum
      };

      localStorage.setItem('SGV_SECURITY_DATA', JSON.stringify(securityData));

    } catch (error) {
      console.error('Erro ao salvar em localStorage:', error);
      throw error;
    }
  }

  /**
   * Obter timestamp da última sincronização
   */
  getLastSyncTime() {
    try {
      const lastSync = localStorage.getItem(this.lastSyncKey);
      return lastSync ? parseInt(lastSync) : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Atualizar timestamp da última sincronização
   */
  updateLastSyncTime(timestamp) {
    try {
      localStorage.setItem(this.lastSyncKey, timestamp.toString());
    } catch (error) {
      console.error('Erro ao atualizar timestamp:', error);
    }
  }

  /**
   * Calcular checksum para integridade de dados
   */
  calculateChecksum(data) {
    const jsonString = JSON.stringify(data);
    return CryptoJS.SHA256(jsonString).toString();
  }

  /**
   * Verificar integridade dos dados armazenados
   */
  verifyIntegrity() {
    try {
      const securityData = JSON.parse(localStorage.getItem('SGV_SECURITY_DATA') || '{}');

      if (!securityData.users || !securityData.checksum) {
        return { valid: false, reason: 'Dados incompletos' };
      }

      // Recalcular checksum
      const calculatedChecksum = this.calculateChecksum(securityData.users);

      if (calculatedChecksum !== securityData.checksum) {
        return { valid: false, reason: 'Checksum inválido - dados corrompidos' };
      }

      return { valid: true };

    } catch (error) {
      return { valid: false, reason: 'Erro na verificação: ' + error.message };
    }
  }

  /**
   * Restaurar dados de segurança
   */
  restoreSecurityData() {
    try {
      const verification = this.verifyIntegrity();

      if (!verification.valid) {
        console.warn('⚠️ Dados de segurança inválidos:', verification.reason);
        return null;
      }

      const securityData = JSON.parse(localStorage.getItem('SGV_SECURITY_DATA'));

      return securityData.users;

    } catch (error) {
      console.error('Erro ao restaurar dados de segurança:', error);
      return null;
    }
  }

  /**
   * Atualizar senha de usuário (chamado quando usuário muda senha)
   */
  async updatePassword(userId, newPassword, username) {
    try {
      console.log('🔐 Atualizando senha do usuário:', username);

      // Gerar novo hash
      const hashData = SecurePasswordHash.hashForStorage(newPassword, username);

      // Atualizar em localStorage
      const legacyUsers = JSON.parse(localStorage.getItem('INIT_USERS') || '[]');
      const userIndex = legacyUsers.findIndex(u => u.id === userId);

      if (userIndex !== -1) {
        // Atualizar apenas dados de senha
        legacyUsers[userIndex].passwordHash = hashData.hash;
        legacyUsers[userIndex].passwordSalt = hashData.salt;
        legacyUsers[userIndex].passwordVersion = '2.0';
        legacyUsers[userIndex].passwordUpdatedAt = new Date().toISOString();
        legacyUsers[userIndex].passwordUpdatedBy = 'self';

        // Remover senha em texto plano se existir
        if (legacyUsers[userIndex].password) {
          delete legacyUsers[userIndex].password;
        }

        // Salvar dados atualizados
        localStorage.setItem('INIT_USERS', JSON.stringify(legacyUsers));

        // Sincronizar com servidor
        await this.syncSecurityData();

        console.log('✅ Senha atualizada e sincronizada com sucesso');

        return { success: true };
      } else {
        return { success: false, error: 'Usuário não encontrado' };
      }

    } catch (error) {
      console.error('❌ Erro ao atualizar senha:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Obter status de sincronização
   */
  getSyncStatus() {
    try {
      const lastSync = this.getLastSyncTime();
      const now = Date.now();
      const minutesSinceSync = Math.floor((now - lastSync) / (60 * 1000));

      const integrity = this.verifyIntegrity();
      const usersData = this.getSecureUsersData();
      const usersWithHash = usersData.filter(u => u.hasHash).length;
      const totalUsers = usersData.length;
      const usersNeedingUpgrade = usersData.filter(u => u.needsUpgrade).length;

      return {
        lastSync: new Date(lastSync),
        minutesSinceSync,
        isSynced: minutesSinceSync < 10,
        integrityValid: integrity.valid,
        integrityMessage: integrity.reason,
        usersWithHash,
        totalUsers,
        usersNeedingUpgrade,
        syncHealth: usersWithHash === totalUsers ? 'healthy' : 'partial'
      };

    } catch (error) {
      return {
        error: error.message,
        syncHealth: 'error'
      };
    }
  }

  /**
   * Inicializar serviço
   */
  initialize() {
    console.log('🚀 Inicializando serviço de sincronização de segurança...');

    // Verificar integridade dos dados
    const integrity = this.verifyIntegrity();
    if (!integrity.valid) {
      console.warn('⚠️ Integridade comprometida:', integrity.reason);
    }

    // Iniciar sincronização automática
    this.startSecuritySync();

    return {
      initialized: true,
      status: this.getSyncStatus()
    };
  }
}

// Exportar instância única
const securitySyncService = new SecuritySyncService();

export default securitySyncService;
